import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { fetchCurrentUser } from './hooks/useUsers'

// Performance monitoring
const performanceMonitor = {
  trackApiCall: (key: string, duration: number) => {
    // Store in memory for now, can be sent to analytics later
    if (window.performance && window.performance.mark) {
      window.performance.mark(`api-${key}-end`)
      window.performance.measure(
        `api-${key}`,
        `api-${key}-start`,
        `api-${key}-end`,
      )
    }

    // Log slow queries
    if (duration > 1000) {
      // intentionally left for future slow-query logging
    }
  },

  trackCacheHit: (key: string) => {
    // Track cache hit rate
    const cacheStats = JSON.parse(localStorage.getItem('cacheStats') || '{}')
    cacheStats[key] = (cacheStats[key] || 0) + 1
    localStorage.setItem('cacheStats', JSON.stringify(cacheStats))
  },
}

// Query Cache with performance tracking
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Global error handling for queries
    if (error instanceof Error) {
      // Don't show error messages for background refetches
      if (query.state.dataUpdateCount === 0) {
        message.error(`Failed to load data: ${error.message}`)
      }
    }
  },
  onSuccess: (_data, query) => {
    // Track successful queries
    performanceMonitor.trackCacheHit(JSON.stringify(query.queryKey))
  },
})

// Mutation Cache with optimistic updates support
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, _mutation) => {
    // Global error handling for mutations
    if (error instanceof Error) {
      message.error(`Operation failed: ${error.message}`)
    }
  },
  onSuccess: (_data, _variables, _context, _mutation) => {
    // Track successful mutations
  },
})

// Enhanced Query Client Configuration
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Stale time: data is fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && error.message.includes('4')) {
          return false
        }
        // Retry up to 3 times with exponential backoff
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch configuration
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      refetchOnMount: true,

      // Network mode
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Retry configuration for mutations
      retry: 1,
      retryDelay: 1000,

      // Network mode
      networkMode: 'offlineFirst',
    },
  },
})

// Query key factory for consistent key generation
const queryKeys = {
  all: ['rita'] as const,

  // Master Data
  masterData: {
    all: () => [...queryKeys.all, 'masterData'] as const,
    lists: () => [...queryKeys.masterData.all(), 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.masterData.lists(), filters] as const,
    details: () => [...queryKeys.masterData.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.masterData.details(), id] as const,
  },

  // Cross Reference
  crossReference: {
    all: () => [...queryKeys.all, 'crossReference'] as const,
    lists: () => [...queryKeys.crossReference.all(), 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.crossReference.lists(), filters] as const,
    details: () => [...queryKeys.crossReference.all(), 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.crossReference.details(), id] as const,
    mappings: () => [...queryKeys.crossReference.all(), 'mappings'] as const,
    mapping: (id: string) =>
      [...queryKeys.crossReference.mappings(), id] as const,
  },

  // Exceptions
  exceptions: {
    all: () => [...queryKeys.all, 'exceptions'] as const,
    lists: () => [...queryKeys.exceptions.all(), 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.exceptions.lists(), filters] as const,
    details: () => [...queryKeys.exceptions.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.exceptions.details(), id] as const,
    stats: () => [...queryKeys.exceptions.all(), 'stats'] as const,
  },

  // Integration Status
  integrationStatus: {
    all: () => [...queryKeys.all, 'integrationStatus'] as const,
    metrics: () => [...queryKeys.integrationStatus.all(), 'metrics'] as const,
    activity: () => [...queryKeys.integrationStatus.all(), 'activity'] as const,
  },

  // Users
  users: {
    all: () => [...queryKeys.all, 'users'] as const,
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all(), 'current'] as const,
  },

  // Configs
  configs: {
    all: () => [...queryKeys.all, 'configs'] as const,
    lists: () => [...queryKeys.configs.all(), 'list'] as const,
    list: (type: string) => [...queryKeys.configs.lists(), type] as const,
  },

  // Partner Systems
  partnerSystems: {
    all: () => [...queryKeys.all, 'partnerSystems'] as const,
    lists: () => [...queryKeys.partnerSystems.all(), 'list'] as const,
    list: () => [...queryKeys.partnerSystems.lists()] as const,
  },
}

// Prefetch utilities
export const prefetchUtils = {
  // Prefetch master data list
  prefetchMasterDataList: async (filters: Record<string, unknown> = {}) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.masterData.list(filters),
      queryFn: () =>
        import('./hooks/useMasterData').then((m) =>
          // Narrow type at call site to avoid explicit any
          (m.fetchMasterDataList as (f: unknown) => unknown)(filters),
        ),
      staleTime: 10 * 60 * 1000, // Consider fresh for 10 minutes
    })
  },

  // Prefetch user data
  prefetchCurrentUser: async () => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.users.current(),
      queryFn: () => fetchCurrentUser(),
      staleTime: 30 * 60 * 1000, // Consider fresh for 30 minutes
    })
  },
}

// Invalidation utilities
export const invalidationUtils = {
  // Invalidate all queries
  invalidateAll: () => queryClient.invalidateQueries(),

  // Invalidate specific entity types
  invalidateMasterData: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.masterData.all() }),
  invalidateCrossReference: () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.crossReference.all(),
    }),
  invalidateExceptions: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.exceptions.all() }),
  invalidateUsers: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),

  // Smart invalidation based on mutation type
  smartInvalidate: (mutationType: string, entityId?: string) => {
    switch (mutationType) {
      case 'createMasterData':
        queryClient.invalidateQueries({
          queryKey: queryKeys.masterData.lists(),
        })
        break
      case 'updateMasterData':
        if (entityId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.masterData.detail(entityId),
          })
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.masterData.lists(),
        })
        break
      case 'deleteMasterData':
        queryClient.invalidateQueries({
          queryKey: queryKeys.masterData.all(),
        })
        break
      case 'createCrossReference':
        queryClient.invalidateQueries({
          queryKey: queryKeys.crossReference.lists(),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.crossReference.mappings(),
        })
        break
      case 'updateCrossReference':
        if (entityId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.crossReference.detail(entityId),
          })
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.crossReference.lists(),
        })
        break
      default:
    }
  },
}
